import { Component, OnInit, OnDestroy, ChangeDetectorRef, HostListener } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { ColumnApi, GridApi, GridOptions } from 'ag-grid-community';
import moment from 'moment';
import { ProjectDetailsApiService } from '../../services/project-details-api.service';
import { ProjectDetailsStateService } from '../../services/project-details-state.service';
import { ProjectDetailsGridConfigModel } from '../../../common/models/project-details-grid-config.model';
import { GridDatePickerComponent } from '../../../common/custom-components/ag-grid-datepicker-component';
import { commonFunctions } from '../../../common/classes/common.class';
import { ProjectTaskRow } from '../../models/project-details.types';

@Component({
  selector: 'app-project-tasks-tab',
  templateUrl: './project-tasks-tab.component.html',
  styleUrls: ['./project-tasks-tab.component.scss']
})
export class ProjectTasksTabComponent implements OnInit, OnDestroy {
  taskForm!: FormGroup;

  employeesList: string[] = [];
  booleanOptions: string[] = ['YES', 'NO'];

  rowData: ProjectTaskRow[] = [];
  columnDefs: any[] = [];
  gridOptions: GridOptions = {};
  agGridAPI!: GridApi;
  agGridColumnAPI!: ColumnApi;

  isSaveEnabled = false;
  isDeleteEnabled = false;
  private isEditing = false;
  private subs = new Subscription();

  components = {
    GridDatePickerComponent: GridDatePickerComponent
  };

  constructor(
    private fb: FormBuilder,
    private cdr: ChangeDetectorRef,
    private apiService: ProjectDetailsApiService,
    public stateService: ProjectDetailsStateService
  ) {}

  ngOnInit(): void {
    this.buildForm();
    this.setupGridConfig();
    this.subscribeToState();
    this.loadReferenceData();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  buildForm(): void {
    this.taskForm = this.fb.group({
      taskRef: ['', Validators.required],
      projectTaskDescription: [''],
      projectTaskManager: [null, Validators.required],
      fixedCostValue: [null],
      fixedCostStart: [null],
      fixedCostEnd: [null],
      isDefault: [false],
      active: [false]
    });

    this.taskForm.valueChanges.subscribe(() => {
      if (this.taskForm.dirty) {
        this.stateService.setUnsavedChanges(true);
      }
    });
  }

  setupGridConfig(): void {
    const gridConfig = new ProjectDetailsGridConfigModel();
    this.columnDefs = gridConfig.projectTasksColumns.map((col: any) => ({
      headerName: col.headerName.split('_').join(' '),
      field: col.field,
      resizable: true,
      sortable: true,
      editable: col.editable,
      cellStyle: col.cellStyle,
      checkboxSelection: col.checkboxSelection,
      headerCheckboxSelection: col.headerCheckboxSelection,
      hide: !!col.hide,
      cellEditor: col.cellEditor || null,
      cellEditorParams: col.cellEditorParams ? {
        ...col.cellEditorParams,
        values: col.field === 'Manager' ? this.employeesList : this.booleanOptions
      } : null,
      valueFormatter: col.valueFormatter || null,
      flex: 1
    }));

    this.gridOptions = {
      rowSelection: 'multiple',
      groupDefaultExpanded: 1,
      groupSelectsChildren: true,
      suppressAggFuncInHeader: true,
      suppressRowClickSelection: true,
      getRowStyle: (params) => {
        if (params.node.isSelected()) {
          return { background: 'lightblue' };
        }
        if (params.node.data?.dirty) {
          return { background: '#ffc107' };
        }
        return undefined;
      }
    };
  }

  subscribeToState(): void {
    this.subs.add(
      this.stateService.tableData$.subscribe(data => {
        if (data.projectTasks) {
          this.rowData = this.transformTaskRows(data.projectTasks);
        } else {
          this.rowData = [];
        }
        this.isSaveEnabled = false;
        this.isDeleteEnabled = false;
        this.cdr.detectChanges();
      })
    );
  }

  transformTaskRows(tasks: any[]): ProjectTaskRow[] {
    return tasks.map(item => {
      const row = { ...item };
      row.Active = commonFunctions.booleanToString(row.Active);
      row.Default = commonFunctions.booleanToString(row.Default);
      if (row.Fixed_Cost_Start && typeof row.Fixed_Cost_Start === 'string') {
        row.Fixed_Cost_Start = row.Fixed_Cost_Start.split('T')[0];
      }
      if (row.Fixed_Cost_End && typeof row.Fixed_Cost_End === 'string') {
        row.Fixed_Cost_End = row.Fixed_Cost_End.split('T')[0];
      }
      return row;
    });
  }

  async loadReferenceData(): Promise<void> {
    try {
      const employees = await this.apiService.getEmployees();
      this.employeesList = employees || [];
      this.setupGridConfig(); // Refresh column cellEditor values
      this.cdr.detectChanges();
    } catch (e) {
      console.error('Error loading task ref data:', e);
    }
  }

  onGridReady(params: any): void {
    this.agGridAPI = params.api;
    this.agGridColumnAPI = params.columnApi;
  }

  onCellValueChanged(event: any): void {
    event.data.isModified = true;
    event.data.dirty = true;
    event.api.applyTransaction({ update: [event.data] });
    event.api.redrawRows({ rowNodes: [event.node] });
    this.isSaveEnabled = true;
    this.stateService.setUnsavedChanges(true);
  }

  onSelectionChanged(event: any): void {
    const selectedNodes = event.api.getSelectedNodes();
    this.isDeleteEnabled = selectedNodes.length > 0;
    event.api.redrawRows({ rowNodes: selectedNodes });
  }

  onCellEditingStarted(event: any): void {
    this.isEditing = true;
  }

  onCellEditingStopped(event: any): void {
    this.isEditing = false;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (this.isEditing && !this.isClickInsideGrid(event)) {
      this.agGridAPI?.stopEditing();
    }
  }

  private isClickInsideGrid(event: MouseEvent): boolean {
    const gridElement = document.querySelector('.ag-theme-balham');
    return gridElement?.contains(event.target as Node) ?? false;
  }

  // ==========================================
  // Form & Table Actions
  // ==========================================

  private validateTaskDates(startDate: any, endDate: any): boolean {

    // End date cannot be entered without a start date
    if (endDate && !startDate) {
      this.stateService.showNotification({
        Type: 'failure',
        Message: 'Start date is required when end date is provided!'
      });
      return false;
    }

    // If both dates are present, start date cannot be after end date
    if (startDate && endDate) {
      const start = moment(startDate);
      const end = moment(endDate);

      if (start.isAfter(end)) {
        this.stateService.showNotification({
          Type: 'failure',
          Message: 'Start date cannot be after end date!'
        });
        return false;
      }
    }

    return true;
  }

  async onSubmitTask(): Promise<void> {
    if (this.taskForm.invalid) {
      this.taskForm.markAllAsTouched();
      alert('Please fill all required task fields.');
      return;
    }

    if (!this.validateTaskDates(
      this.taskForm.get('fixedCostStart')?.value,
      this.taskForm.get('fixedCostEnd')?.value
    )) {
      return;
    }

    const formVal = this.taskForm.value;
    const clientCode = this.stateService.getSelectedClientCode();
    const projectCode = this.stateService.getSelectedProjectCode();

    const payload = {
      taskName: formVal.taskRef,
      taskDescription: formVal.projectTaskDescription || '',
      client: clientCode,
      project: projectCode,
      isDefault: !!formVal.isDefault,
      manager: formVal.projectTaskManager,
      fixedCostValue: formVal.fixedCostValue
        ? parseInt(formVal.fixedCostValue, 10)
        : 0,
      fixedCostStart: formVal.fixedCostStart
        ? moment(formVal.fixedCostStart).format('YYYY-MM-DD')
        : null,
      fixedCostEnd: formVal.fixedCostEnd
        ? moment(formVal.fixedCostEnd).format('YYYY-MM-DD')
        : null,
      isActive: !!formVal.active,
      loginName: this.stateService.loginName,
      roleName: '',
      task_id:0
    };

    try {
      const res = await this.apiService.saveProjectTask([payload]).toPromise();
      this.stateService.showNotification(res);
      this.taskForm.reset({ isDefault: false, active: true });

      const data = await this.apiService.getProjectDetailsTables(clientCode, projectCode);
      this.stateService.setTableData(data);
    } catch (err: any) {
      this.stateService.showNotification(err.error || { Type: 'failure', Message: 'Failed to add project task' });
    }
  }

  async onSaveTasks(): Promise<void> {
    const clientCode = this.stateService.getSelectedClientCode();
    const projectCode = this.stateService.getSelectedProjectCode();
    const modifiedNodes: any[] = [];

    this.agGridAPI.forEachNode(node => {
      if (node.data?.isModified) {
        modifiedNodes.push(node.data);
      }
    });

    if (modifiedNodes.length === 0) return;

    // Validate dates for modified grid rows
    for (const row of modifiedNodes) {
      if (!this.validateTaskDates(
        row.Fixed_Cost_Start,
        row.Fixed_Cost_End
      )) {
        return;
      }
    }

    const payloadArray = modifiedNodes.map(row => ({
      task_id: row.Task_id,
      taskName: row.Task_Reference,
      taskDescription: row.Description || '',
      client: clientCode,
      project: projectCode,
      isDefault: row.Default === 'YES' || row.Default === true,
      manager: row.Manager,
      fixedCostValue: row.Fixed_Cost_Value
        ? parseInt(row.Fixed_Cost_Value, 10)
        : 0,
      fixedCostStart: row.Fixed_Cost_Start
        ? moment(row.Fixed_Cost_Start).format('YYYY-MM-DD')
        : null,
      fixedCostEnd: row.Fixed_Cost_End
        ? moment(row.Fixed_Cost_End).format('YYYY-MM-DD')
        : null,
      isActive: row.Active === 'YES' || row.Active === true,
      loginName: this.stateService.loginName,
      roleName: ''
    }));

    try {
      await this.apiService.updateProjectTasks(payloadArray).toPromise();
      this.stateService.showNotification({ Type: 'success', Message: 'Project tasks updated successfully' });
      this.isSaveEnabled = false;
      this.stateService.setUnsavedChanges(false);

      const data = await this.apiService.getProjectDetailsTables(clientCode, projectCode);
      this.stateService.setTableData(data);
    } catch (err: any) {
      this.stateService.showNotification(err.error || { Type: 'failure', Message: 'Failed to update tasks' });
    }
  }

  async onDeleteTasks(): Promise<void> {
    const clientCode = this.stateService.getSelectedClientCode();
    const projectCode = this.stateService.getSelectedProjectCode();
    const selectedRows = this.agGridAPI.getSelectedRows();

    if (selectedRows.length === 0) return;

    const nonDefaultRows = selectedRows.filter(r => r.Default !== 'YES');
    if (nonDefaultRows.length === 0) {
      alert('Default task cannot be deleted.');
      return;
    }

    const payloadArray = nonDefaultRows.map(row => ({
      client: clientCode,
      project: projectCode,
      loginname: this.stateService.loginName,
      task_id: row.Task_id
    }));

    try {
      const res = await this.apiService.deleteProjectTasks(payloadArray).toPromise();
      this.stateService.showNotification(res);
      this.isDeleteEnabled = false;

      const data = await this.apiService.getProjectDetailsTables(clientCode, projectCode);
      this.stateService.setTableData(data);
    } catch (err: any) {
      this.stateService.showNotification(err.error || { Type: 'failure', Message: 'Failed to delete tasks' });
    }
  }

  onClearForm(): void {
    this.taskForm.reset({ isDefault: false, active: true });
  }

  onNextTab(): void {
    this.stateService.setSelectedIndex(3);
  }
}

